import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Search,
  Shield,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Info,
  Loader2,
  RefreshCw,
  ExternalLink,
  Link2,
  FileSearch,
  MapPin,
  Target,
  Activity,
  ChevronLeft,
  ChevronRight,
  Filter,
  XCircle,
  X,
  Copy,
  Clock,
  BarChart3,
  TrendingUp,
  Eye,
  EyeOff,
  Play,
  Globe,
  Layers,
  List,
  ArrowUpDown,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import SEO from './SEO';
import api, {
  SEODashboard,
  SEOFinding,
  SEOChangeEvent,
  LinkEquityScore,
  KeywordPageMapping,
  CoverageGap,
  CrawlRun,
} from '../services/api';
import { useClient } from '../contexts/ClientContext';

type TabType = 'overview' | 'technical' | 'local' | 'links' | 'keywords' | 'changes' | 'findings' | 'console';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  info: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
};

const SEVERITY_CHART_COLORS: Record<string, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#3b82f6',
  info: '#6b7280',
};

const TABS: { id: TabType; label: string; icon: typeof Shield }[] = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'technical', label: 'Technical', icon: FileSearch },
  { id: 'local', label: 'Local SEO', icon: MapPin },
  { id: 'links', label: 'Links', icon: Link2 },
  { id: 'keywords', label: 'Keywords', icon: Target },
  { id: 'changes', label: 'Changes', icon: Activity },
  { id: 'findings', label: 'All Findings', icon: List },
  { id: 'console', label: 'Search Console', icon: Search },
];

const PAGE_SIZE = 25;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const severityBadge = (severity: string) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
      SEVERITY_COLORS[severity] || SEVERITY_COLORS.info
    }`}
  >
    {severity}
  </span>
);

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateShort = (dateStr: string | null | undefined) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const truncateUrl = (url: string, maxLen = 60) => {
  if (!url) return '';
  if (url.length <= maxLen) return url;
  return url.slice(0, maxLen) + '...';
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SEOAudit = () => {
  const { selectedClientId } = useClient();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dashboard state
  const [dashboard, setDashboard] = useState<SEODashboard | null>(null);
  const [crawlRuns, setCrawlRuns] = useState<CrawlRun[]>([]);

  // Tab data states
  const [technicalFindings, setTechnicalFindings] = useState<SEOFinding[]>([]);
  const [localFindings, setLocalFindings] = useState<SEOFinding[]>([]);
  const [coverageGaps, setCoverageGaps] = useState<CoverageGap[]>([]);
  const [linkEquity, setLinkEquity] = useState<LinkEquityScore[]>([]);
  const [keywordMappings, setKeywordMappings] = useState<KeywordPageMapping[]>([]);
  const [changeEvents, setChangeEvents] = useState<SEOChangeEvent[]>([]);
  const [allFindings, setAllFindings] = useState<SEOFinding[]>([]);

  // Filter states
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [engineFilter, setEngineFilter] = useState<string>('all');
  const [resolvedFilter, setResolvedFilter] = useState<string>('unresolved');
  const [changeTypeFilter, setChangeTypeFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Loading states per tab
  const [tabLoading, setTabLoading] = useState(false);
  const [triggeringCrawl, setTriggeringCrawl] = useState(false);
  const [crawlInProgress, setCrawlInProgress] = useState(false);
  const [crawlProgress, setCrawlProgress] = useState<{ pages: number; status: string }>({ pages: 0, status: '' });
  const [dataVersion, setDataVersion] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedFinding, setSelectedFinding] = useState<SEOFinding | null>(null);

  // ---------- Data loading ----------

  const loadDashboard = useCallback(async () => {
    if (!selectedClientId) return;
    try {
      setLoading(true);
      setError(null);
      const [dashData, crawlData, findingsData] = await Promise.all([
        api.getSEODashboard(selectedClientId),
        api.getSEOCrawlRuns(selectedClientId),
        api.getSEOFindings({ client: selectedClientId, is_resolved: false }),
      ]);
      setDashboard(dashData);
      setCrawlRuns(crawlData.results || []);
      setAllFindings(findingsData.results || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load SEO audit dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Tab-specific data loading
  useEffect(() => {
    if (!selectedClientId) return;

    const loadTabData = async () => {
      setTabLoading(true);
      try {
        switch (activeTab) {
          case 'technical': {
            const params: Record<string, string> = { client: selectedClientId, engine: 'technical' };
            if (severityFilter !== 'all') params.severity = severityFilter;
            const data = await api.getSEOFindings(params);
            setTechnicalFindings(data.results || []);
            break;
          }
          case 'local': {
            const [findingsData, gapsData] = await Promise.all([
              api.getSEOFindings({ client: selectedClientId, engine: 'local_seo' }),
              api.getSEOCoverageGaps({ client: selectedClientId }),
            ]);
            setLocalFindings(findingsData.results || []);
            setCoverageGaps(gapsData.results || []);
            break;
          }
          case 'links': {
            const data = await api.getSEOLinkEquity({ client: selectedClientId });
            setLinkEquity(data.results || []);
            break;
          }
          case 'keywords': {
            const [mappings, gaps] = await Promise.all([
              api.getSEOKeywordMappings(selectedClientId),
              api.getSEOCoverageGaps({ client: selectedClientId, gap_type: 'keyword' }),
            ]);
            setKeywordMappings(mappings.results || []);
            setCoverageGaps(gaps.results || []);
            break;
          }
          case 'changes': {
            const data = await api.getSEOChangeEvents(selectedClientId);
            setChangeEvents(data.results || []);
            break;
          }
          case 'findings': {
            const params: Record<string, string> = { client: selectedClientId };
            if (engineFilter !== 'all') params.engine = engineFilter;
            if (severityFilter !== 'all') params.severity = severityFilter;
            if (resolvedFilter === 'resolved') params.is_resolved = 'true';
            else if (resolvedFilter === 'unresolved') params.is_resolved = 'false';
            const data = await api.getSEOFindings(params);
            setAllFindings(data.results || []);
            setCurrentPage(1);
            break;
          }
          default:
            break;
        }
      } catch (err) {
        console.error('Failed to load tab data:', err);
      } finally {
        setTabLoading(false);
      }
    };

    if (activeTab !== 'overview' && activeTab !== 'console') {
      loadTabData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedClientId, severityFilter, engineFilter, resolvedFilter, dataVersion]);

  // ---------- Actions ----------

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startCrawlPolling = useCallback(() => {
    if (!selectedClientId) return;
    setCrawlInProgress(true);
    setCrawlProgress({ pages: 0, status: 'pending' });
    stopPolling();

    pollRef.current = setInterval(async () => {
      try {
        const runsData = await api.getSEOCrawlRuns(selectedClientId);
        const runs = runsData.results || [];
        const active = runs.find((r: CrawlRun) => r.status === 'running' || r.status === 'pending');

        if (active) {
          setCrawlProgress({ pages: active.pages_crawled || 0, status: active.status });
        } else {
          // Crawl finished — stop polling and refresh all data
          stopPolling();
          setCrawlInProgress(false);
          setCrawlProgress({ pages: 0, status: '' });
          await loadDashboard();
          // Bump version to force tab data reload
          setDataVersion((v) => v + 1);
        }
      } catch {
        // ignore polling errors
      }
    }, 3000);
  }, [selectedClientId, stopPolling, loadDashboard]);

  // Cleanup polling on unmount or client change
  useEffect(() => {
    return () => stopPolling();
  }, [selectedClientId, stopPolling]);

  // Check for in-progress crawl on initial load
  useEffect(() => {
    if (!selectedClientId) return;
    (async () => {
      try {
        const runsData = await api.getSEOCrawlRuns(selectedClientId);
        const runs = runsData.results || [];
        const active = runs.find((r: CrawlRun) => r.status === 'running' || r.status === 'pending');
        if (active) {
          startCrawlPolling();
        }
      } catch {
        // ignore
      }
    })();
  }, [selectedClientId, startCrawlPolling]);

  const handleTriggerCrawl = async () => {
    if (!selectedClientId) return;
    try {
      setTriggeringCrawl(true);
      await api.triggerCrawl(selectedClientId);
      startCrawlPolling();
    } catch (err) {
      console.error('Failed to trigger crawl:', err);
      setError('Failed to trigger crawl');
    } finally {
      setTriggeringCrawl(false);
    }
  };

  const handleResolve = async (findingId: string) => {
    try {
      setActionLoading(findingId);
      await api.resolveSEOFinding(findingId);
      // Refresh current tab data
      setAllFindings((prev) => prev.map((f) => (f.id === findingId ? { ...f, is_resolved: true } : f)));
      setTechnicalFindings((prev) => prev.map((f) => (f.id === findingId ? { ...f, is_resolved: true } : f)));
      setLocalFindings((prev) => prev.map((f) => (f.id === findingId ? { ...f, is_resolved: true } : f)));
    } catch (err) {
      console.error('Failed to resolve finding:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuppress = async (findingId: string) => {
    try {
      setActionLoading(findingId);
      await api.suppressSEOFinding(findingId);
      setAllFindings((prev) => prev.map((f) => (f.id === findingId ? { ...f, is_suppressed: true } : f)));
      setTechnicalFindings((prev) => prev.map((f) => (f.id === findingId ? { ...f, is_suppressed: true } : f)));
      setLocalFindings((prev) => prev.map((f) => (f.id === findingId ? { ...f, is_suppressed: true } : f)));
    } catch (err) {
      console.error('Failed to suppress finding:', err);
    } finally {
      setActionLoading(null);
    }
  };

  // ---------- Derived data ----------

  const severityChartData = useMemo(() => {
    if (!dashboard?.findings_by_severity) return [];
    return Object.entries(dashboard.findings_by_severity)
      .map(([severity, count]) => ({ severity, count }))
      .sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99));
  }, [dashboard]);

  const engineChartData = useMemo(() => {
    if (!dashboard?.findings_by_engine) return [];
    return Object.entries(dashboard.findings_by_engine).map(([engine, count]) => ({
      name: engine,
      value: count,
    }));
  }, [dashboard]);

  const PIE_COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#6366f1'];

  // Technical findings filtered
  const filteredTechnicalFindings = useMemo(() => {
    let items = technicalFindings;
    if (severityFilter !== 'all') {
      items = items.filter((f) => f.severity === severityFilter);
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(
        (f) =>
          f.rule_id.toLowerCase().includes(q) ||
          f.page_url.toLowerCase().includes(q) ||
          f.observation.toLowerCase().includes(q)
      );
    }
    return items.sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99));
  }, [technicalFindings, severityFilter, searchTerm]);

  // Links derived
  const sortedByPageRank = useMemo(
    () => [...linkEquity].sort((a, b) => b.internal_pagerank - a.internal_pagerank),
    [linkEquity]
  );
  const orphanPages = useMemo(() => linkEquity.filter((p) => p.is_orphan), [linkEquity]);
  const underlinkedPages = useMemo(
    () =>
      [...linkEquity]
        .filter((p) => p.inlinks_count < 3 && !p.is_orphan)
        .sort((a, b) => a.inlinks_count - b.inlinks_count)
        .slice(0, 20),
    [linkEquity]
  );

  // Keywords derived
  const cannibalized = useMemo(() => keywordMappings.filter((k) => k.is_cannibalized), [keywordMappings]);

  // Changes filtered
  const filteredChanges = useMemo(() => {
    let items = changeEvents;
    if (changeTypeFilter !== 'all') {
      items = items.filter((e) => e.change_type === changeTypeFilter);
    }
    return items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [changeEvents, changeTypeFilter]);

  const changeTypes = useMemo(() => {
    const types = new Set(changeEvents.map((e) => e.change_type));
    return Array.from(types).sort();
  }, [changeEvents]);

  // All findings paginated
  const paginatedFindings = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return allFindings.slice(start, start + PAGE_SIZE);
  }, [allFindings, currentPage]);

  const totalPages = Math.max(1, Math.ceil(allFindings.length / PAGE_SIZE));

  // Copy all findings to clipboard as formatted text
  const copyAllFindings = async () => {
    const severityOrder = ['critical', 'high', 'medium', 'low', 'info'];
    const grouped: Record<string, SEOFinding[]> = {};

    allFindings.forEach((f) => {
      if (!grouped[f.severity]) grouped[f.severity] = [];
      grouped[f.severity].push(f);
    });

    let text = '# SEO Recommendations Report\n\n';
    text += `Generated: ${new Date().toLocaleDateString()}\n`;
    text += `Total: ${allFindings.length} findings\n\n`;

    severityOrder.forEach((sev) => {
      const findings = grouped[sev];
      if (!findings?.length) return;

      text += `## ${sev.charAt(0).toUpperCase() + sev.slice(1)} (${findings.length})\n\n`;

      findings.forEach((f, idx) => {
        text += `### ${idx + 1}. ${f.rule_id}\n`;
        if (f.page_url) text += `**Page:** ${f.page_url}\n`;
        text += `**Observation:** ${f.observation}\n`;
        if (f.recommendation) text += `**Recommendation:** ${f.recommendation}\n`;
        text += `**Priority Score:** ${f.priority_score?.toFixed(1) || 'N/A'}\n`;
        text += '\n---\n\n';
      });
    });

    try {
      await navigator.clipboard.writeText(text);
      // Brief visual feedback could be added here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const engineOptions = useMemo(() => {
    const engines = new Set(allFindings.map((f) => f.engine));
    return Array.from(engines).sort();
  }, [allFindings]);

  // ---------- No client guard ----------

  if (!selectedClientId) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center">
        <div className="text-center">
          <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Client Selected</h3>
          <p className="text-gray-500 dark:text-gray-400">
            Please select a client to view the SEO audit dashboard.
          </p>
        </div>
      </div>
    );
  }

  // ---------- Loading ----------

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  // ---------- Render helpers ----------

  const renderCrawlStatus = () => {
    const crawl = dashboard?.crawl_status?.latest_crawl;
    if (!crawl) {
      return (
        <span className="text-gray-500 dark:text-gray-400 text-xs">No crawls yet</span>
      );
    }
    const statusColor =
      crawl.status === 'completed'
        ? 'text-green-600 dark:text-green-400'
        : crawl.status === 'running'
        ? 'text-blue-600 dark:text-blue-400'
        : crawl.status === 'failed'
        ? 'text-red-600 dark:text-red-400'
        : 'text-gray-600 dark:text-gray-400';

    return (
      <div className="flex items-center gap-2">
        <span className={`text-xs font-medium uppercase ${statusColor}`}>{crawl.status}</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {crawl.pages_crawled} pages | {formatDateShort(crawl.started_at)}
        </span>
      </div>
    );
  };

  const renderFindingsTable = (
    findings: SEOFinding[],
    showEngine = false,
    showActions = false
  ) => (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              {showEngine && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Engine
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Rule
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Page
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Severity
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Observation
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Recommendation
              </th>
              {showActions && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {findings.length === 0 ? (
              <tr>
                <td
                  colSpan={showEngine && showActions ? 7 : showEngine || showActions ? 6 : 5}
                  className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                >
                  No findings found.
                </td>
              </tr>
            ) : (
              findings.map((f) => (
                <tr
                  key={f.id}
                  onClick={() => setSelectedFinding(f)}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer ${
                    f.is_resolved ? 'opacity-50' : ''
                  } ${f.is_suppressed ? 'opacity-40 line-through' : ''}`}
                >
                  {showEngine && (
                    <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {f.engine}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-gray-900 dark:text-white">{f.rule_id}</span>
                  </td>
                  <td className="px-4 py-3">
                    <a
                      href={f.page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1 text-xs text-primary-500 hover:text-primary-600"
                      title={f.page_url}
                    >
                      <span className="truncate max-w-[200px]">{truncateUrl(f.page_url, 50)}</span>
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  </td>
                  <td className="px-4 py-3">{severityBadge(f.severity)}</td>
                  <td className="px-4 py-3 text-xs text-gray-700 dark:text-gray-300 max-w-xs">
                    <span className="line-clamp-2">{f.observation}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400 max-w-xs">
                    <span className="line-clamp-2">{f.recommendation}</span>
                  </td>
                  {showActions && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {!f.is_resolved && !f.is_suppressed && (
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleResolve(f.id); }}
                            disabled={actionLoading === f.id}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-300 dark:hover:bg-green-900/40 rounded transition-colors disabled:opacity-50"
                            title="Mark as resolved"
                          >
                            {actionLoading === f.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <CheckCircle className="w-3 h-3" />
                            )}
                            Resolve
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSuppress(f.id); }}
                            disabled={actionLoading === f.id}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                            title="Suppress finding"
                          >
                            <EyeOff className="w-3 h-3" />
                            Suppress
                          </button>
                        </div>
                      )}
                      {f.is_resolved && (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                          <CheckCircle className="w-3 h-3" /> Resolved
                        </span>
                      )}
                      {f.is_suppressed && !f.is_resolved && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <EyeOff className="w-3 h-3" /> Suppressed
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ---------- Tab renderers ----------

  const renderOverview = () => {
    const totalFindings = Object.values(dashboard?.findings_by_severity || {}).reduce(
      (sum, c) => sum + c,
      0
    );
    const criticalCount = dashboard?.findings_by_severity?.critical || 0;
    const highCount = dashboard?.findings_by_severity?.high || 0;
    const mediumCount = dashboard?.findings_by_severity?.medium || 0;
    const lowCount = dashboard?.findings_by_severity?.low || 0;
    const infoCount = dashboard?.findings_by_severity?.info || 0;

    return (
      <div className="space-y-3">
        {/* Severity Score Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-1">
              <Layers className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400">Total</span>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{totalFindings}</p>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-red-200 dark:border-red-800">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400">Critical</span>
            </div>
            <p className="text-xl font-bold text-red-600 dark:text-red-400">{criticalCount}</p>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400">High</span>
            </div>
            <p className="text-xl font-bold text-orange-600 dark:text-orange-400">{highCount}</p>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-yellow-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400">Medium</span>
            </div>
            <p className="text-xl font-bold text-yellow-600 dark:text-yellow-400">{mediumCount}</p>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 mb-1">
              <Info className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400">Low</span>
            </div>
            <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{lowCount}</p>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-1">
              <Info className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-500 dark:text-gray-400">Info</span>
            </div>
            <p className="text-xl font-bold text-gray-600 dark:text-gray-400">{infoCount}</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Severity Distribution Bar Chart */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              Findings by Severity
            </h3>
            {severityChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={severityChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                  <XAxis
                    dataKey="severity"
                    tick={{ fontSize: 12, fill: '#9ca3af' }}
                    axisLine={{ stroke: '#4b5563' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#9ca3af' }}
                    axisLine={{ stroke: '#4b5563' }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#f3f4f6',
                    }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {severityChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={SEVERITY_CHART_COLORS[entry.severity] || '#6b7280'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-gray-500 dark:text-gray-400 text-sm">
                No severity data available
              </div>
            )}
          </div>

          {/* Findings by Engine Pie */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              Findings by Engine
            </h3>
            {engineChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={engineChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {engineChartData.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#f3f4f6',
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: '12px', color: '#9ca3af' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-gray-500 dark:text-gray-400 text-sm">
                No engine data available
              </div>
            )}
          </div>
        </div>

        {/* Bottom Row: Crawl Status + Coverage Gaps + Priorities */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Crawl Status */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Latest Crawl</h3>
              <button
                onClick={handleTriggerCrawl}
                disabled={triggeringCrawl || crawlInProgress}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-primary-500 text-black rounded hover:bg-primary-600 transition-colors disabled:opacity-50"
              >
                {triggeringCrawl ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Play className="w-3 h-3" />
                )}
                New Crawl
              </button>
            </div>
            {renderCrawlStatus()}
            {crawlRuns.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {crawlRuns.slice(0, 5).map((run) => (
                  <div
                    key={run.id}
                    className="flex items-center justify-between text-xs p-2 bg-gray-50 dark:bg-gray-800 rounded"
                  >
                    <div className="flex items-center gap-2">
                      <Clock className="w-3 h-3 text-gray-400" />
                      <span className="text-gray-700 dark:text-gray-300">
                        {formatDateShort(run.started_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500 dark:text-gray-400">
                        {run.pages_crawled} pages
                      </span>
                      <span
                        className={`font-medium uppercase ${
                          run.status === 'completed'
                            ? 'text-green-600 dark:text-green-400'
                            : run.status === 'running'
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {run.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Coverage Gaps Count */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Coverage Gaps</h3>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <MapPin className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {dashboard?.coverage_gap_count ?? 0}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Service/city combinations without pages
                </p>
              </div>
            </div>
            <button
              onClick={() => setActiveTab('local')}
              className="mt-3 text-xs text-primary-500 hover:text-primary-600 font-medium"
            >
              View Local SEO details
            </button>
          </div>

          {/* Top Priorities */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Top Priorities</h3>
            {dashboard?.top_priorities && dashboard.top_priorities.length > 0 ? (
              <div className="space-y-2">
                {dashboard.top_priorities.slice(0, 5).map((f) => (
                  <div
                    key={f.id}
                    className="flex items-start gap-2 text-xs p-2 bg-gray-50 dark:bg-gray-800 rounded"
                  >
                    <div className="mt-0.5">{severityBadge(f.severity)}</div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 dark:text-white truncate">{f.rule_id}</p>
                      <p className="text-gray-500 dark:text-gray-400 line-clamp-1">{f.observation}</p>
                    </div>
                    <span className="text-gray-400 text-[10px] whitespace-nowrap">
                      P{f.priority_score}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">No priorities identified yet.</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTechnical = () => (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by rule, URL, or observation..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm"
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="info">Info</option>
          </select>
        </div>
      </div>

      {tabLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {filteredTechnicalFindings.length} technical finding{filteredTechnicalFindings.length !== 1 ? 's' : ''}
          </p>
          {renderFindingsTable(filteredTechnicalFindings, false, true)}
        </>
      )}
    </div>
  );

  const renderLocal = () => (
    <div className="space-y-3">
      {tabLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
        </div>
      ) : (
        <>
          {/* SAB Findings */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                Local SEO Findings ({localFindings.length})
              </h2>
            </div>
            {localFindings.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Rule
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Severity
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Observation
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Recommendation
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {localFindings.map((f) => (
                      <tr key={f.id} onClick={() => setSelectedFinding(f)} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer">
                        <td className="px-4 py-3 text-xs font-mono text-gray-900 dark:text-white">
                          {f.rule_id}
                        </td>
                        <td className="px-4 py-3">{severityBadge(f.severity)}</td>
                        <td className="px-4 py-3 text-xs text-gray-700 dark:text-gray-300 max-w-xs">
                          {f.observation}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400 max-w-xs">
                          {f.recommendation}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                No local SEO findings.
              </div>
            )}
          </div>

          {/* Coverage Gaps Matrix */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                Coverage Gaps ({coverageGaps.length})
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Service keyword and city combinations missing dedicated pages
              </p>
            </div>
            {coverageGaps.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Service Keyword
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        City
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Gap Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Market
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Existing Page
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {coverageGaps.map((gap) => (
                      <tr key={gap.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3 text-xs font-medium text-gray-900 dark:text-white">
                          {gap.service_keyword}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-700 dark:text-gray-300">{gap.city}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                            {gap.gap_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                          {gap.market_city}, {gap.market_region}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {gap.existing_page_url ? (
                            <a
                              href={gap.existing_page_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-500 hover:text-primary-600 flex items-center gap-1"
                            >
                              <span className="truncate max-w-[180px]">{truncateUrl(gap.existing_page_url, 40)}</span>
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            </a>
                          ) : (
                            <span className="text-red-500 dark:text-red-400 font-medium">Missing</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                No coverage gaps detected.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderLinks = () => (
    <div className="space-y-3">
      {tabLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
        </div>
      ) : (
        <>
          {/* Top Pages by PageRank */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-purple-500" />
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Top Pages by Internal PageRank
                </h2>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Page
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      PageRank
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Inlinks
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Outlinks
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Hub Score
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {sortedByPageRank.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        No link equity data. Run a crawl first.
                      </td>
                    </tr>
                  ) : (
                    sortedByPageRank.slice(0, 20).map((page) => (
                      <tr key={page.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3">
                          <a
                            href={page.page_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-primary-500 hover:text-primary-600"
                          >
                            <span className="truncate max-w-[300px]">{truncateUrl(page.page_url)}</span>
                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          </a>
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-medium text-gray-900 dark:text-white">
                          {page.internal_pagerank.toFixed(4)}
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-gray-600 dark:text-gray-400">
                          {page.inlinks_count}
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-gray-600 dark:text-gray-400">
                          {page.outlinks_count}
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-gray-600 dark:text-gray-400">
                          {page.hub_score.toFixed(4)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Orphan Pages */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Orphan Pages ({orphanPages.length})
                </h2>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Pages with zero internal inlinks -- unreachable by crawlers
              </p>
            </div>
            <div className="p-4">
              {orphanPages.length === 0 ? (
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  No orphan pages detected.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {orphanPages.map((page) => (
                    <div
                      key={page.id}
                      className="flex items-center justify-between p-2 bg-red-50 dark:bg-red-900/10 rounded text-xs"
                    >
                      <a
                        href={page.page_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-primary-500 hover:text-primary-600 truncate"
                      >
                        {truncateUrl(page.page_url)}
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                      <span className="text-red-600 dark:text-red-400 font-medium whitespace-nowrap ml-2">
                        0 inlinks
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Underlinked Money Pages */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-amber-500" />
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Underlinked Pages ({underlinkedPages.length})
                </h2>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Pages with fewer than 3 internal inlinks
              </p>
            </div>
            {underlinkedPages.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Page
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Inlinks
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        PageRank
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {underlinkedPages.map((page) => (
                      <tr key={page.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3">
                          <a
                            href={page.page_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-primary-500 hover:text-primary-600"
                          >
                            <span className="truncate max-w-[300px]">{truncateUrl(page.page_url)}</span>
                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          </a>
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-medium text-amber-600 dark:text-amber-400">
                          {page.inlinks_count}
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-gray-600 dark:text-gray-400">
                          {page.internal_pagerank.toFixed(4)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                No underlinked pages found.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderKeywords = () => (
    <div className="space-y-3">
      {tabLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
        </div>
      ) : (
        <>
          {/* Cannibalization Warnings */}
          {cannibalized.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <h3 className="text-sm font-semibold text-red-800 dark:text-red-300">
                  Cannibalization Warnings ({cannibalized.length})
                </h3>
              </div>
              <p className="text-xs text-red-600 dark:text-red-400 mb-3">
                These keywords are targeted by multiple pages, which may dilute ranking potential.
              </p>
              <div className="space-y-1.5">
                {cannibalized.slice(0, 10).map((k) => (
                  <div
                    key={k.id}
                    className="flex items-center justify-between p-2 bg-white dark:bg-gray-900 rounded text-xs"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-gray-900 dark:text-white">{k.full_query}</span>
                      <span className="text-gray-400 ml-2">target: {truncateUrl(k.target_page_url, 40)}</span>
                    </div>
                    <span className="text-red-600 dark:text-red-400 font-medium ml-2 whitespace-nowrap">
                      Pos {Number(k.gsc_position).toFixed(1)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Keyword-Page Mapping Table */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                Keyword-Page Mappings ({keywordMappings.length})
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Keyword
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      City
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Target Page
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Clicks
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Impressions
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      CTR
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Position
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {keywordMappings.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        No keyword mappings available.
                      </td>
                    </tr>
                  ) : (
                    keywordMappings.map((k) => (
                      <tr
                        key={k.id}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                          k.is_cannibalized ? 'bg-red-50/50 dark:bg-red-900/5' : ''
                        }`}
                      >
                        <td className="px-4 py-3 text-xs font-medium text-gray-900 dark:text-white">
                          {k.keyword}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                          {k.city_modifier || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <a
                            href={k.target_page_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-primary-500 hover:text-primary-600"
                          >
                            <span className="truncate max-w-[200px]">{truncateUrl(k.target_page_url, 40)}</span>
                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          </a>
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-gray-900 dark:text-white">
                          {Number(k.gsc_clicks).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-gray-600 dark:text-gray-400">
                          {Number(k.gsc_impressions).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-gray-600 dark:text-gray-400">
                          {(Number(k.gsc_ctr) * 100).toFixed(2)}%
                        </td>
                        <td className="px-4 py-3 text-right text-xs">
                          <span
                            className={
                              Number(k.gsc_position) <= 10
                                ? 'text-green-600 dark:text-green-400'
                                : Number(k.gsc_position) <= 20
                                ? 'text-yellow-600 dark:text-yellow-400'
                                : 'text-red-600 dark:text-red-400'
                            }
                          >
                            {Number(k.gsc_position).toFixed(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {k.is_cannibalized ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                              <AlertTriangle className="w-3 h-3" />
                              Cannibalized
                            </span>
                          ) : k.is_primary ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                              <CheckCircle className="w-3 h-3" />
                              Primary
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">Secondary</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Coverage Gaps */}
          {coverageGaps.length > 0 && (
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Keyword Coverage Gaps ({coverageGaps.length})
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Service Keyword
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        City
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Gap Type
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {coverageGaps.map((gap) => (
                      <tr key={gap.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3 text-xs font-medium text-gray-900 dark:text-white">
                          {gap.service_keyword}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-700 dark:text-gray-300">{gap.city}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                            {gap.gap_type}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderChanges = () => (
    <div className="space-y-3">
      {/* Filter */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={changeTypeFilter}
            onChange={(e) => setChangeTypeFilter(e.target.value)}
            className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm"
          >
            <option value="all">All Change Types</option>
            {changeTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {filteredChanges.length} event{filteredChanges.length !== 1 ? 's' : ''}
        </span>
      </div>

      {tabLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
        </div>
      ) : filteredChanges.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
          <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">No change events recorded yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredChanges.map((event) => (
            <div
              key={event.id}
              className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="mt-0.5">{severityBadge(event.severity)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-gray-900 dark:text-white uppercase">
                        {event.change_type}
                      </span>
                      <a
                        href={event.page_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-primary-500 hover:text-primary-600 truncate"
                      >
                        {truncateUrl(event.page_url, 50)}
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                    </div>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                      {event.old_value && (
                        <div className="p-2 bg-red-50 dark:bg-red-900/10 rounded text-xs">
                          <span className="text-red-600 dark:text-red-400 font-medium">Old: </span>
                          <span className="text-gray-700 dark:text-gray-300 break-all">{event.old_value}</span>
                        </div>
                      )}
                      {event.new_value && (
                        <div className="p-2 bg-green-50 dark:bg-green-900/10 rounded text-xs">
                          <span className="text-green-600 dark:text-green-400 font-medium">New: </span>
                          <span className="text-gray-700 dark:text-gray-300 break-all">{event.new_value}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-3">
                  <Clock className="w-3 h-3 inline mr-1" />
                  {formatDateShort(event.created_at)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderAllFindings = () => (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={engineFilter}
            onChange={(e) => setEngineFilter(e.target.value)}
            className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm"
          >
            <option value="all">All Engines</option>
            {engineOptions.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        </div>
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm"
        >
          <option value="all">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
          <option value="info">Info</option>
        </select>
        <select
          value={resolvedFilter}
          onChange={(e) => setResolvedFilter(e.target.value)}
          className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm"
        >
          <option value="all">All Status</option>
          <option value="unresolved">Unresolved</option>
          <option value="resolved">Resolved</option>
        </select>
        {(engineFilter !== 'all' || severityFilter !== 'all' || resolvedFilter !== 'unresolved') && (
          <button
            onClick={() => {
              setEngineFilter('all');
              setSeverityFilter('all');
              setResolvedFilter('unresolved');
            }}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <XCircle className="w-3 h-3" />
            Clear filters
          </button>
        )}
        <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
          {allFindings.length} finding{allFindings.length !== 1 ? 's' : ''}
        </span>
      </div>

      {tabLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
        </div>
      ) : (
        <>
          {renderFindingsTable(paginatedFindings, true, true)}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Page {currentPage} of {totalPages} ({allFindings.length} total)
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-3 h-3" />
                  Previous
                </button>
                {/* Page number buttons */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        currentPage === pageNum
                          ? 'bg-primary-500 text-black'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  // ---------- Main render ----------

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary-500" />
                SEO
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                SEO audit, recommendations &amp; Search Console analytics
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleTriggerCrawl}
                disabled={triggeringCrawl}
                className="flex items-center gap-2 bg-primary-500 text-black px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
              >
                {triggeringCrawl ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                Trigger Crawl
              </button>
              <button
                onClick={loadDashboard}
                disabled={loading}
                className="flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={copyAllFindings}
                disabled={allFindings.length === 0}
                className="flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                title="Copy all findings to clipboard as markdown"
              >
                <Copy className="w-4 h-4" />
                Copy All
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 py-3">
        {/* Error Banner */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-2 flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-sm text-red-700 hover:text-red-900 underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-3 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSearchTerm('');
                setCurrentPage(1);
              }}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-primary-500 text-black'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {crawlInProgress ? (
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Crawl in progress{crawlProgress.status === 'pending' ? ' (queued)' : ''}...
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {crawlProgress.pages > 0 ? `${crawlProgress.pages} pages crawled. ` : ''}
              Recommendations will appear once the crawl and analysis are complete.
            </p>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'technical' && renderTechnical()}
            {activeTab === 'local' && renderLocal()}
            {activeTab === 'links' && renderLinks()}
            {activeTab === 'keywords' && renderKeywords()}
            {activeTab === 'changes' && renderChanges()}
            {activeTab === 'findings' && renderAllFindings()}
            {activeTab === 'console' && <SEO embedded />}
          </>
        )}
      </div>

      {/* Finding Detail Modal */}
      {selectedFinding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSelectedFinding(null)}>
          <div
            className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between p-5 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start gap-3 min-w-0">
                {severityBadge(selectedFinding.severity)}
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white font-mono">
                    {selectedFinding.rule_id}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {selectedFinding.engine} &middot; {formatDate(selectedFinding.created_at)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedFinding(null)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 overflow-y-auto max-h-[calc(85vh-130px)] space-y-4">
              {/* Page URL */}
              {selectedFinding.page_url && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Page</h4>
                  <a
                    href={selectedFinding.page_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 break-all"
                  >
                    {selectedFinding.page_url}
                    <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                  </a>
                </div>
              )}

              {/* Observation */}
              {selectedFinding.observation && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Observation</h4>
                  <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{selectedFinding.observation}</p>
                </div>
              )}

              {/* Recommendation */}
              {selectedFinding.recommendation && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Recommendation</h4>
                  <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{selectedFinding.recommendation}</p>
                </div>
              )}

              {/* Scores */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Priority</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{selectedFinding.priority_score?.toFixed(1) ?? '—'}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Impact</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{selectedFinding.impact_score?.toFixed(1) ?? '—'}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Effort</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{selectedFinding.effort_score?.toFixed(1) ?? '—'}</p>
                </div>
              </div>

              {/* Confidence */}
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-500 dark:text-gray-400">Confidence:</span>
                <span className="font-medium text-gray-900 dark:text-white">{selectedFinding.confidence}</span>
              </div>

              {/* Metrics */}
              {selectedFinding.metrics && Object.keys(selectedFinding.metrics).length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Metrics</h4>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-1.5">
                    {Object.entries(selectedFinding.metrics).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-xs">
                        <span className="text-gray-500 dark:text-gray-400 font-mono">{key}</span>
                        <span className="text-gray-900 dark:text-white font-medium ml-4 text-right break-all">
                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status */}
              <div className="flex items-center gap-3 text-sm">
                {selectedFinding.is_resolved && (
                  <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                    <CheckCircle className="w-4 h-4" /> Resolved {selectedFinding.resolved_at ? `on ${formatDate(selectedFinding.resolved_at)}` : ''}
                  </span>
                )}
                {selectedFinding.is_suppressed && (
                  <span className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400">
                    <EyeOff className="w-4 h-4" /> Suppressed
                  </span>
                )}
                {!selectedFinding.is_resolved && !selectedFinding.is_suppressed && (
                  <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                    <AlertCircle className="w-4 h-4" /> Open
                  </span>
                )}
              </div>
            </div>

            {/* Footer actions */}
            {!selectedFinding.is_resolved && !selectedFinding.is_suppressed && (
              <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => { handleSuppress(selectedFinding.id); setSelectedFinding(null); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <EyeOff className="w-3.5 h-3.5" />
                  Suppress
                </button>
                <button
                  onClick={() => { handleResolve(selectedFinding.id); setSelectedFinding(null); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                  Resolve
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SEOAudit;
